import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { SpreadingState } from '@components/main/spreading/state/manager';
import { AutocompleteOption, Row } from '@components/main/spreading/models/models';

const SELECT_PLACEHOLDER = 'Select line item for offset adjustment';
// Gap between the trigger and the overlaid dropdown panel, in pixels.
const DROPDOWN_GAP = 4;

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

@Component({
  selector: 'app-categorization-offset-adjustment-view',
  templateUrl: './offset-adjustment-view.component.html',
  styleUrls: ['./offset-adjustment-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class OffsetAdjustmentViewComponent {
  @Input() state: SpreadingState;
  @Input() row: Row;

  @Output() insertOffsetAdjustment = new EventEmitter<AutocompleteOption>();

  @ViewChild('dropdownTrigger') dropdownTrigger?: ElementRef<HTMLElement>;

  readonly selectPlaceholder = SELECT_PLACEHOLDER;
  selectedOffsetItem: AutocompleteOption | null = null;
  isDropdownOpen = false;
  dropdownPosition: DropdownPosition | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  get manualItemLabel(): string {
    return this.row?.userFacingLabel || this.row?.label || '';
  }

  get selectedOffsetItemId(): number | null {
    return this.selectedOffsetItem?.id ?? null;
  }

  get triggerLabel(): string {
    return this.selectedOffsetItem?.value ?? this.selectPlaceholder;
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.updateDropdownPosition();
    }
    this.cdr.detectChanges();
  }

  /**
   * Anchors the overlaid dropdown panel directly below the trigger. The panel
   * is fixed-positioned so it escapes the modal's overflow clipping and can
   * extend below the modal without changing the modal's height.
   */
  private updateDropdownPosition(): void {
    const triggerEl = this.dropdownTrigger?.nativeElement;
    if (!triggerEl) {
      return;
    }
    const rect = triggerEl.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom + DROPDOWN_GAP,
      left: rect.left,
      width: rect.width,
    };
  }

  onOffsetItemSelected(option: AutocompleteOption): void {
    this.selectedOffsetItem = option;
    this.isDropdownOpen = false;
    this.cdr.detectChanges();
    this.dropdownTrigger?.nativeElement.focus();
  }

  onInsertItems(): void {
    if (!this.selectedOffsetItem) {
      return;
    }
    this.insertOffsetAdjustment.emit(this.selectedOffsetItem);
  }
}
